import { Timestamp } from 'firebase/firestore';
import { Moment } from 'moment-timezone';

import TWithAudit from '../commons/withAudit.interface';
import TWithUserID from '../commons/withUserId.interface';
import { TDeliverStates } from './deliverStates.interface';
import TMembers from './members.interface';
import TRepetition from './repetition.interface';
import TScheduleJobs from './scheduleJobs.interface';
import TScheduleTrigger from './scheduleTrigger.interface';
import { TEventTypeSummary } from './eventTypeSummary.interface';
import TTaskSummary from './taskSummary.interface';

/**
 * Sync with: services/src/schemas/TSchedule.ts
 */

export interface TCron {
  endAt: Timestamp;
  repetition: TRepetition;
  startAt: Timestamp;
  timeZone: string;
}

export default interface TSchedule extends TWithAudit, TWithUserID, TDeliverStates {
  divId: string;
  duration: number; // in minutes
  id: string;
  jobs: TScheduleJobs;
  members: TMembers;
  name: string;
  reminders: number[];
  schedule: TCron;
  triggers: TScheduleTrigger[];
  forms: TEventTypeSummary[]; // add in elastic

  currentExecutionId?: string; // This and nextExecutionId should never be set at the same time
  nextExecutionId?: string; // This and currentExecutionId should never be set at the same time
  type: ScheduleType;
  isExpiredAllowed: boolean;
  expiredAllowedTime?: number; // 0 = no limit
  taskSummary?: TTaskSummary;
  triggersFollowIndex: boolean;
}

export enum ScheduleType {
  Schedule = 'schedule',
  Task = 'task',
  CheckpointRoute = 'checkpoint_route',
}

/** Sparse Elastic `_source` for a short schedule result (global search). */
export interface ScheduleElasticSummarySource {
  id?: string;
  divId?: string;
  name?: string;
  type?: string;
  schedule?: { startAt?: unknown };
}

export interface ScheduleSearchSummary {
  id: string;
  divisionId: string;
  name: string;
  type: ScheduleType;
  startAtMs: number | null;
}

export interface ScheduleSearchSummariesParams {
  orgId: string;
  divsIds: string[];
  searchTerm: string;
  types: ScheduleType[];
  pageSize?: number;
}

export interface DeleteScheduleParams {
  organizationId: string;
  divisionId: string;
  scheduleId: string;
}

export interface DeleteScheduleResponse {
  success: boolean;
  scheduleId: string;
}

type ScheduleServerManagedField =
  | 'jobs'
  | 'currentExecutionId'
  | 'nextExecutionId'
  | 'uid'
  | 'createdAt'
  | 'updatedAt'
  | 'delivered'
  | 'viewed';

export type ScheduleSaveInput = Omit<TSchedule, ScheduleServerManagedField>;

export interface SaveScheduleParams {
  organizationId: string;
  divisionId: string;
  schedule: ScheduleSaveInput;
}

/**
 * Wire representation of the schedule sent to the `saveSchedule` callable.
 * Firestore `Timestamp` objects do not survive the callable JSON boundary:
 * - cron `startAt` / `endAt` travel as epoch millis (converted in ScheduleRepository)
 * - nested Timestamps (`forms[].components[].dateValue`, member `birthDate`,
 *   embedded location audits) may arrive as JSON maps; services
 *   `ScheduleSaveService.normalizeScheduleInput` coerces them to admin Timestamps
 *   before persist (same model as services `TComponent.dateValue` / `TCron`).
 */
export interface ScheduleSaveCronWire extends Omit<TCron, 'startAt' | 'endAt'> {
  startAt: number;
  endAt: number;
}

export type ScheduleSaveInputWire = Omit<ScheduleSaveInput, 'schedule'> & {
  schedule: ScheduleSaveCronWire;
};

export interface SaveScheduleParamsWire {
  organizationId: string;
  divisionId: string;
  schedule: ScheduleSaveInputWire;
}

export interface SaveScheduleResponse {
  success: boolean;
  scheduleId: string;
}

export type ScheduleOptionAction = 'NOW' | 'NEXT';

export interface ScheduleOptionInput {
  action: ScheduleOptionAction;
}

export interface CreateScheduleOptionParams {
  organizationId: string;
  divisionId: string;
  scheduleId: string;
  action: ScheduleOptionAction;
}

export interface ScheduleFormValues {
  divId?: string;
  duration: number;
  endDate?: Moment | null;
  name: string;
  reminder: number;
  repetition: TRepetition;
  startDate?: Moment;
  startTime?: number;
  timeZone: string;
  triggersFollowIndex?: boolean;
}

export interface ScheduleFormSaveContext {
  values: ScheduleFormValues;
  members: TMembers;
  triggers: TScheduleTrigger[];
  startAt: Moment;
  endDateEnabled: boolean;
  isRecurrent: boolean;
}
